import type { TutorialStepId } from "./tutorialSteps";

/**
 * Presenter voiceovers for the App Guide — separate from tip copy.
 * Speak like a live walkthrough, not a reading of the slide.
 */
export const TUTORIAL_NARRATION: Partial<Record<TutorialStepId, string>> = {
  "nav-hotlist":
    "Start your day here. Hotlist is the queue Intentico builds for you — the highest-priority emails, calls, and LinkedIn touches waiting to be worked. We're about to open it.",

  "hotlist-actions":
    "This is your action queue for the sample Summitlane workspace. The headline tells you how much is on your plate today, and the channel pills beneath it break the work into email, call, and LinkedIn.",

  "team-rep-filter":
    "If you lead a team, you can narrow Hotlist to a specific rep. That way you're coaching their queue, not drowning in the whole book of business at once.",

  "hotlist-schedule":
    "Not everything has to be today. Use the schedule control to jump days, snap back to Today, or flip on Full week when you want the whole picture.",

  "hotlist-view-switch":
    "You're looking at one way to work the list. Leaders often park here in approval mode, while reps live in the action list — same Hotlist, different jobs.",

  "hotlist-work-action":
    "When you're ready to grind, this is how you open work. Each control launches the action modal with the insight and draft already loaded for that row.",

  "hotlist-column-picker":
    "Tables get noisy fast. The column picker lets you show only what you care about — hide the rest so scanning stays quick.",

  "hotlist-table-rows":
    "Every row is a concrete next step on a real account in this sample portfolio. In a moment we'll open one so you can see the full action experience.",

  "hotlist-action-context":
    "Here's the why. The left side of the action modal explains the insight or contact signal behind the recommendation before you ever reach for the draft.",

  "hotlist-action-prompt":
    "And here's the what to say. Intentico drafts subject and body from that signal — edit freely, then copy or open your mail client when it sounds like you.",

  "hotlist-action-footer":
    "Finish the loop with these footer actions — send, copy, or mark done. Completing here keeps Hotlist, digests, and activity history honest.",

  "nav-accounts":
    "Next stop is Accounts — your full portfolio. Search, assign owners, and open any company workspace from this rail.",

  "accounts-portfolio":
    "This is the book of business table for Summitlane's tour snapshot. Each row is an account you can drill into, assign, or expand for more context.",

  "accounts-search":
    "When you know the company but not the row, search is fastest. Type a name or domain and the table narrows as you go.",

  "accounts-add":
    "To add an account, open Actions and choose Add account. Reps typically request accounts; leaders and admins can create them depending on your workspace rules.",

  "accounts-open":
    "Click the company name to enter the account workspace — calendar, insights, and contacts all live there.",

  "accounts-row-expand":
    "Need a peek without leaving the list? Expand the row for that account's Narrative radar trend summary — or the company overview if none is available yet — then jump in when you're ready.",

  "accounts-bulk-assign":
    "Leaders can reassign in bulk. Select the accounts you care about, then open Assign and choose Assign reps or Sales leader to update ownership.",

  "accounts-row-assign":
    "Or change ownership on a single row. Same idea as bulk assign — just scoped to this account.",

  "accounts-enter":
    "You're in. This is the account page for our sample company — the hub for plan, signals, and people.",

  "account-masthead-meta":
    "The masthead keeps identity and status visible while you move between tabs — so you never lose which account you're coaching.",

  "account-calendar-tab":
    "Switch to Calendar when you want touches on a timeline. It's the same action types you saw on Hotlist, just for this company.",

  "account-calendar-action":
    "Click any plan item to open the familiar work modal. Completing here updates Hotlist too — one system of record.",

  "account-calendar-view-toggle":
    "Flip between day and list layouts depending on whether you're sequencing a day or scanning the stretch ahead.",

  "account-insights-tab":
    "Insights is where account intel piles up — signals and deeper rows you can turn into outreach.",

  "account-signals-overview":
    "Signals summarize what's interesting right now. Think of them as headlines pointing you at conversations worth having.",

  "account-signals-actions":
    "From a signal you can share, complete, or dismiss — same triage pattern you'll see everywhere intent shows up.",

  "account-insights-view-toggle":
    "Prefer a denser table? Switch views here. Signals for scanning themes; table for sorting and comparison.",

  "account-insights-table":
    "The table lists account insights with scores and status. Expand a row when you want the full synopsis without leaving this page.",

  "account-insights-row-expand":
    "Expanded detail gives you the story behind the score — enough context to decide whether to act now or archive it.",

  "account-insights-table-actions":
    "These icons open social prompts, complete, or dismiss for the insight. We'll go deep on that flow in Outreach.",

  "account-contacts-tab":
    "Contacts are the people you actually message. Open this tab when you're picking a persona for outreach.",

  "account-contacts":
    "Here's the contact list for this account — titles, emails, and scores that help you choose who to engage first.",

  "account-contacts-row-expand":
    "Expand a contact for enrichment and commonality detail so your first note isn't generic.",

  "account-contacts-actions":
    "From the contact row you can jump into outbound flows tied to that person — including social prompts.",

  "account-contacts-reorder":
    "Drag to reshape decision-maker order when filters aren't narrowing the list. It becomes your working priority ladder.",

  "account-contacts-social":
    "The share icon on a contact launches the same outreach workspace you get from insights — grounded in this person and account.",

  "outreach-icons":
    "This share icon is the front door to outreach. You'll see it on insights, assets, contacts, and Hotlist — one control, same workspace.",

  "social-prompt-sequence":
    "Inside outreach, the left rail is your cadence. Each step is a channel — pick one and the draft loads in the center.",

  "social-prompt-compose":
    "The center panel is the living draft for the step you selected. Edit until it sounds like you, then send or copy from the footer.",

  "social-prompt-contacts":
    "Recipient context sits here so you know exactly who this step is aimed at before you hit send.",

  "social-prompt-footer":
    "Footer actions finish the step — open LinkedIn or email, copy the message, or move on when you're done.",

  "social-prompt-request-more":
    "Need a different cadence? Request more opens a setup flow to ask Intentico for a new sequence instead of improvising from scratch.",

  "social-prompt-request-setup":
    "Set the ask here — what you're trying to accomplish — so the generated sequence matches the moment you're in.",

  "social-prompt-request-cadence":
    "Shape the channel mix and length of the sequence. Think of it as briefing the writer before they draft.",

  "social-prompt-request-recipient":
    "Confirm who the sequence is for. Wrong persona wasted weeks of follow-ups — get this right once.",

  "social-prompt-request":
    "When the brief looks good, submit the request. You'll get a fresh multi-step cadence back in this same workspace.",

  "insight-complete-icon":
    "After you use an insight, tap complete. That opens the dialog for logging what you did.",

  "insight-complete":
    "Complete is how you log emails, calls, and LinkedIn touches, add a note, and clear the insight from your homepage timeline — it still lives on account pages.",

  "insight-dismiss-icon":
    "If the insight isn't relevant, dismiss from the X. Prefer complete when you acted; dismiss when it never should have been there.",

  "insight-dismiss":
    "Dismiss is permanent, so leave a quick reason when you can — that feedback sharpens future recommendations for everyone.",

  "nav-insights":
    "Industry insights go beyond one account. Open Insights when you need market and sector stories to bring into any conversation.",







  "nav-assets":
    "Assets is your approved collateral library — decks, one-pagers, and files sales can share without hunting SharePoint.",

  "assets-library":
    "Browse what's cleared for customers. Each row is something you can open, share via prompts, or expand for detail.",

  "assets-row-expand":
    "Expanded detail shows metadata and description so you know you're grabbing the right leave-behind.",

  "assets-social-prompt":
    "Share from an asset to draft outreach that stays on-brand with that collateral in mind.",

  "assets-add":
    "Admins and owners can upload new assets here when marketing refreshes the kit — keep the library current.",

  "nav-settings":
    "Settings is where personal preferences and org admin tools live. We'll walk the sections that matter for day-to-day use.",

  "settings-quick-actions":
    "Help and troubleshooting brings together guides, system status, cache refresh, and dismissed messages.",

  "settings-preferences":
    "Personal preferences control your email digests, notifications, outreach voice, and cadence templates.",

  "settings-digest":
    "Digests summarize what deserves attention. Tune frequency and content so email stays useful instead of noisy.",

  "settings-notifications":
    "Notification settings decide which events ping you. Dial them in so coaching signals aren't lost in the flood.",

  "settings-outreach":
    "Outreach defaults shape how prompts and cadences behave for your user — voice, tone, and starting points.",

  "settings-cadence-templates":
    "Cadence templates are reusable multi-step patterns. Leaders standardize good sequences here for the team.",

  "settings-organization":
    "Organization settings are admin territory — company-wide rules that apply beyond your personal prefs.",

  "settings-client-setup":
    "Platform admins configure client setup here — onboarding a workspace and its baseline structure.",

  "settings-billing":
    "Client admins manage billing for their organization — seats, plan, and invoices without leaving Intentico.",

  "settings-org-digest":
    "Organization digest defaults control delivery mode. Manage individual action visibility in Digest recipients.",

  "settings-access":
    "Access controls who can see what. Keep roles tight so reps, leaders, and admins each get the right surface.",

  "settings-team":
    "Team settings manage who sits under each leader — assignments that drive filters across Hotlist and accounts.",

  finish:
    "You're set. Reopen App Guide anytime from the sidebar if you want a refresher — and go work the real queue with confidence.",
};

export function getTutorialNarration(
  stepId: TutorialStepId | undefined,
): string | undefined {
  if (!stepId) return undefined;
  const text = TUTORIAL_NARRATION[stepId];
  return text?.trim() ? text : undefined;
}
